import type { PrintSections } from '@/components/chefs/print/print-root'
import { PrintHeader } from '@/components/print/print-header'
import type { TocEntry } from '@/lib/chefs/print-plan'

interface PrintCoverPageProps {
  kitchenName: string
  rangeDateLabel: string
  sections: PrintSections
  dayCount: number
  uniqueCampusCount: number
  uniqueRecipeCount: number
  /** Range-wide serving total across every campus — the production number
   *  Ops added up school-by-school. Absent on legacy callers. */
  totalServings?: number
  toc: TocEntry[]
}

export function PrintCoverPage({
  kitchenName,
  rangeDateLabel,
  sections,
  dayCount,
  uniqueCampusCount,
  uniqueRecipeCount,
  totalServings,
  toc,
}: PrintCoverPageProps) {
  return (
    <div className="font-sans text-black">
      <PrintHeader kitchenName={kitchenName} dateLabel={rangeDateLabel} />
      <header className="mt-12 border-b-2 border-black pb-4">
        <p className="text-xs uppercase tracking-wide">Print packet</p>
        <h1 className="mt-2 text-4xl font-semibold leading-tight">{kitchenName}</h1>
        <p className="mt-2 text-lg">{rangeDateLabel}</p>
      </header>

      <section className="mt-8 grid grid-cols-4 gap-4">
        <CoverStat label={dayCount === 1 ? 'Day' : 'Days'} value={dayCount} />
        <CoverStat label={uniqueCampusCount === 1 ? 'Campus' : 'Campuses'} value={uniqueCampusCount} />
        {totalServings !== undefined && totalServings > 0 ? (
          <CoverStat label="Total servings" value={totalServings} />
        ) : null}
        {sections.recipes ? (
          <CoverStat label={uniqueRecipeCount === 1 ? 'Unique recipe' : 'Unique recipes'} value={uniqueRecipeCount} />
        ) : null}
      </section>

      <section className="mt-8">
        <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Contents</h2>
        <ol className="mt-3 flex flex-col gap-1.5 text-sm">
          {toc.map((entry, i) =>
            entry.depth === 1 ? (
              // A stop under its day's Summary entry: a packer pulls one pile's sheet from here.
              <li
                key={`${entry.label}-${i}`}
                data-depth="1"
                className="flex items-baseline gap-2 pl-5 text-[13px] text-[#444]">
                <span>{entry.label}</span>
                <span
                  aria-hidden
                  className="min-w-4 flex-1 translate-y-[-2px] border-b border-dotted border-black/20"
                />
                <span className="tabular-nums">{entry.page}</span>
              </li>
            ) : (
              <li key={`${entry.label}-${i}`} className="flex items-baseline gap-2">
                <span className="font-medium">{entry.label}</span>
                <span
                  aria-hidden
                  className="min-w-4 flex-1 translate-y-[-2px] border-b border-dotted border-black/30"
                />
                <span className="font-medium tabular-nums">{entry.page}</span>
              </li>
            ),
          )}
        </ol>
      </section>
    </div>
  )
}

function CoverStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-black/15 p-4">
      <div className="text-3xl font-semibold leading-none">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  )
}
